import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useNavigate, useNavigationType } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MarketplaceListSection from "./MarketplaceListSection";
import {
    fetchMarketplaceListings,
    fetchMarketplaceStats,
    moderateListing,
    fetchMarketplaceCategories,
    fetchMarketplaceConfig,
    deleteUserListing
} from "../../redux/slice/marketplaceSlice";
import { fetchActiveCities, fetchActiveStates } from "../../redux/slice/locationSlice";
import MarketPlaceFilter from "./MarketPlaceFilter";
import {
    PublishedIcon,
    TotalItemsIcon,
    RejectIcon,
    MarketPlaceIcon
} from "../../assets/icons";
import CommonButton from "../../components/commonButton";
import { useToast } from "../../context/TostContext";
import CommonRejectModal from "../../components/reject-modal/RejectModal";
import MarketplaceUserListingModal from "./MarketplaceUserListingModal";
import CommonDeletePopup from "../../components/delete-popup";
import { usePermission } from "../../hooks/usePermission";
import { routes } from "../../routes/pageRoutes";
import { TabCard } from "../../components/custom-card/Cards";
import { FilterBtn } from "../../components/commonButton/btns";
import { TableSearch } from "../../components/form/Fields";

const MODULE_SLUG = "marketplace_listings";


export default function MarketplaceList() {
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const dispatch = useDispatch();
    const toast = useToast();


    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Marketplace / </span>User Listing
            </h4>
        </div>
    );

    const [limit, setLimit] = useState(() => {
        if (navigationType === "POP") {
            const savedLimit = sessionStorage.getItem("marketplace_limit");
            if (savedLimit) return JSON.parse(savedLimit);
        }
        return { value: 10, label: 10 };
    });

    const [currentPage, setCurrentPage] = useState(() => {
        if (navigationType === "POP") {
            const savedPage = sessionStorage.getItem("marketplace_page");
            if (savedPage) return parseInt(savedPage, 10);
        }
        return 1;
    });

    const [cardStatus, setCardStatus] = useState(() => {
        if (navigationType === "POP") {
            return sessionStorage.getItem("marketplace_cardStatus") || "";
        }
        return "";
    });

    const [searchTerm, setSearchTerm] = useState(() => {
        return navigationType === "POP" ? sessionStorage.getItem("marketplace_searchTerm") || "" : "";
    });

    const [filterModal, setFilterModal] = useState(false);
    const [filterValues, setFilterValues] = useState(() => {
        if (navigationType === "POP") {
            const saved = sessionStorage.getItem("marketplace_filters");
            if (saved) return JSON.parse(saved);
        }
        return {
            category_id: [],
            state_id: [],
            city_id: [],
            min_price: "",
            max_price: ""
        };
    });

    const [rejectModal, setRejectModal] = useState({ show: false, itemId: null });
    const [userListingModal, setUserListingModal] = useState({ show: false, editData: null, isView: false });
    const [deleteModal, setDeleteModal] = useState({ show: false, itemId: null });

    const { hasPermission } = usePermission();
    const canCreate = hasPermission(MODULE_SLUG, "create");
    const canUpdate = hasPermission(MODULE_SLUG, "update");
    const canDelete = hasPermission(MODULE_SLUG, "delete");

    const { listings, pagination, stats, categories, config, isLoading, isInitialLoad } = useSelector((state) => state.marketplace);

    const MarketplaceStatsCards = [
        { id: 1, total: stats?.listing_stats?.all || 0, title: "All", icon: <TotalItemsIcon />, status: "", iconClassName: "icon-secondary" },
        { id: 2, total: stats?.listing_stats?.published || 0, title: "Published", icon: <PublishedIcon />, status: "published", iconClassName: "icon-info" },
        { id: 3, total: stats?.listing_stats?.closed || 0, title: "Closed", icon: <PublishedIcon />, status: "closed", iconClassName: "icon-secondary" },
        { id: 4, total: stats?.listing_stats?.rejected || 0, title: "Rejected", icon: <RejectIcon />, status: "rejected", iconClassName: "icon-danger" },
    ];

    const handleCardClick = (value) => {
        setCardStatus(value);
        setCurrentPage(1);
    };

    const isInitialFetchRef = useRef(true);
    const searchTimeout = useRef(null);

    const getListings = useCallback((isDebounced = false) => {
        const params = {
            page: currentPage,
            limit: limit?.value,
            search: searchTerm,
            status: cardStatus,
            seller_type: 'user',
            ...filterValues
        };

        const fetchList = () => {
            dispatch(fetchMarketplaceListings(params));
        };

        if (isDebounced) {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(fetchList, 500);
        } else {
            fetchList();
        }

        sessionStorage.setItem("marketplace_limit", JSON.stringify(limit));
        sessionStorage.setItem("marketplace_page", currentPage.toString());
        sessionStorage.setItem("marketplace_cardStatus", cardStatus);
        sessionStorage.setItem("marketplace_searchTerm", searchTerm);
        sessionStorage.setItem("marketplace_filters", JSON.stringify(filterValues));

    }, [dispatch, currentPage, limit, searchTerm, cardStatus, filterValues]);

    useEffect(() => {
        getListings(isInitialFetchRef.current ? false : true);
        isInitialFetchRef.current = false;
    }, [getListings]);

    useEffect(() => {
        dispatch(fetchMarketplaceStats({ seller_type: 'user' }));
        dispatch(fetchMarketplaceCategories());
        dispatch(fetchMarketplaceConfig());
    }, [dispatch]);

    const fetchStatesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveStates({ search, page, limit: 20 })).unwrap();
    }, [dispatch]);

    const fetchCitiesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveCities({
            search,
            page,
            limit: 20,
            state_ids: filterValues.state_id?.length > 0 ? filterValues.state_id.join(',') : undefined
        })).unwrap();
    }, [dispatch, filterValues.state_id]);

    const handleModerate = async (id, status) => {
        try {
            const result = await dispatch(moderateListing({ id, status, seller_type: 'user' })).unwrap();
            toast.success(result.message || `Listing ${status} successfully`);
            dispatch(fetchMarketplaceStats({ seller_type: 'user' }));
            getListings();
        } catch (error) {
            toast.error(error || "Failed to moderate listing");
        }
    };

    const actionList = [
        {
            title: "View",
            handler: (row) => {
                const targetRoute = row.seller_type === 'company'
                    ? routes.viewMarketplaceBulkOffer.replace(":id", row.id)
                    : routes.viewMarketplaceListing.replace(":id", row.id);
                navigate(targetRoute, { state: { currentPage: currentPage } });
            }
        },
        {
            title: "Edit",
            handler: (row) => {
                setUserListingModal({ show: true, editData: row, isView: false });
            },
            isVisible: (row) => row?.seller_type === 'user' && canUpdate
        },
        {
            title: "Close",
            handler: (row) => handleModerate(row.id, 'closed'),
            isVisible: (row) => row?.status === 'published' && canUpdate
        },
        {
            title: "Reject",
            handler: (row) => setRejectModal({ show: true, itemId: row.id }),
            isVisible: (row) => ['published', 'pending', 'approved'].includes(row?.status) && canUpdate
        },
        {
            title: "Publish",
            handler: (row) => handleModerate(row.id, 'published'),
            isVisible: (row) => ['rejected', 'closed', 'approved'].includes(row?.status) && canUpdate
        },
        {
            title: "Approve",
            handler: (row) => handleModerate(row.id, 'approved'),
            isVisible: (row) => row?.status === 'pending' && canUpdate
        },
        {
            title: "Delete",
            handler: (row) => setDeleteModal({ show: true, itemId: row.id }),
            isVisible: (row) => row?.seller_type === 'user' && canDelete,
            className: "text-danger"
        }
    ];

    const handleRejectSubmit = (reason) => {
        dispatch(moderateListing({ id: rejectModal.itemId, status: 'rejected', rejection_reason: reason, seller_type: 'user' }))
            .then(() => {
                dispatch(fetchMarketplaceStats({ seller_type: 'user' }));
                getListings();
                setRejectModal({ show: false, itemId: null });
                toast.success("Listing rejected successfully");
            })
            .catch((error) => {
                toast.error(error || "Failed to reject listing");
            });
    };

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                <div className="grid gap-4">
                    {MarketplaceStatsCards.map((card, idx) => (
                        <TabCard
                            key={idx}
                            title={card.title}
                            count={card.total}
                            icon={card.icon}
                            onClick={() => handleCardClick(card.status)}
                            active={cardStatus === card.status}
                            iconClassName={card.iconClassName}
                            className="g-col-4 g-col-xxl-3"
                        />
                    ))}
                </div>

                <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
                    <div className="p-4 d-flex align-items-center justify-content-between border-bottom flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">

                            <TableSearch placeholder="Search items..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }} />
                            <FilterBtn onClick={() => setFilterModal(true)} />

                        </div>
                        {canCreate && (
                            <CommonButton
                                title="Add User Listing"
                                icon={<MarketPlaceIcon size={16} />}
                                variant="primary"
                                onClick={() => setUserListingModal({ show: true, editData: null, isView: false })}
                            />
                        )}
                    </div>

                    <MarketplaceListSection
                        data={listings}
                        navigate={navigate}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={limit.value}
                        setPageSize={(val) => {
                            setLimit({ value: val, label: val });
                            setCurrentPage(1);
                        }}
                        totalItems={pagination?.total}
                        isLoading={isLoading}
                        isInitialLoad={isInitialLoad}
                        actionList={actionList}
                    />
                </div>
                <CommonRejectModal
                    isOpen={rejectModal.show}
                    onClose={() => setRejectModal({ show: false, itemId: null })}
                    onReject={handleRejectSubmit}
                    title="Reject Listing"
                    className="common-reject-modal"
                />

                <MarketplaceUserListingModal
                    isOpen={userListingModal.show}
                    onClose={() => setUserListingModal({ show: false, editData: null, isView: false })}
                    editData={userListingModal.editData}
                    isView={userListingModal.isView}
                    onSuccess={() => {
                        getListings();
                        dispatch(fetchMarketplaceStats({ seller_type: 'user' }));
                    }}
                    title={userListingModal.isView ? "View User Listing" : userListingModal.editData ? "Edit User Listing" : "Add User Listing"}
                    size="lg"
                    className="common-modal"
                />

                <CommonDeletePopup
                    show={deleteModal.show}
                    onHide={() => setDeleteModal({ show: false, itemId: null })}
                    onClickSave={async () => {
                        try {
                            await dispatch(deleteUserListing(deleteModal.itemId)).unwrap();
                            toast.success("Listing deleted successfully");
                            dispatch(fetchMarketplaceStats({ seller_type: 'user' }));
                            getListings();
                            setDeleteModal({ show: false, itemId: null });
                        } catch (error) {
                            toast.error(error || "Failed to delete listing");
                        }
                    }}
                    title="Delete User Listing"
                    content="Are you sure you want to delete this user listing? This action cannot be undone."
                    saveTitle="Delete"
                />

                <MarketPlaceFilter
                    isOpen={filterModal}
                    onClose={() => setFilterModal(false)}
                    title="Advanced Filters"
                    size="lg"
                    className="common-modal"
                    categories={categories}
                    config={config}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    fetchStatesOptions={fetchStatesOptions}
                    fetchCitiesOptions={fetchCitiesOptions}
                    onApply={() => {
                        setCurrentPage(1);
                    }}
                    onClear={() => {
                        setFilterValues({
                            category_id: [],
                            state_id: [],
                            city_id: [],
                            min_price: config.price_bounds.min,
                            max_price: config.price_bounds.max
                        });
                        setCurrentPage(1);
                    }}
                />
            </Container>
        </>
    );
}
